<template>
  <div
    v-if="dealCompShow"
    class="button-widge"
    :style="box"
    :class="{ 'no-permission': !dealCompCtrl }"
  >
    <el-button
      v-if="prop?.model === 'base'"
      class="base"
      :type="btnType"
      :size="prop?.size"
      :[prop?.style]="true"
      :plain="btnPlain"
      :class="{ underline: prop?.underline }"
      :style="btnStyle"
      :link="isText"
      @click="click"
    >
      <svg-icon v-if="prop?.icon" :icon-class="prop?.icon" />
      <template v-if="isStatusBtn">{{ showTxt(status) }}</template
      >{{ prop?.txt }}
    </el-button>
    <template v-else>
      <el-button-group
        class="group"
        :type="btnType"
        :size="prop?.size"
        :link="isText"
        :class="{ underline: prop?.underline }"
      >
        <template v-for="v in prop?.options" :key="v.value">
          <el-button
            :[prop?.style]="true"
            :style="btnStyle"
            :plain="btnPlain"
            @click="groupClick(v.value)"
          >
            <svg-icon v-if="v.icon" :icon-class="v.icon" />
            {{ v.label }}
          </el-button>
        </template>
      </el-button-group>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import { useCommonForGeneral } from '@haohan/clw-hooks'
  import type { ComponentOperationData } from '@haohan/clw-event'
  import { buildApiQueryByEvent, composeColor, hasProp } from '@haohan/clw-utils'
  import { find, hasIn } from 'lodash-es'

  const props = defineProps<{
    item: Widget
  }>()

  const {
    styleProp,
    item,
    prop,
    box,
    dealCompShow,
    dealCompCtrl,
    onlyReceiveEvent,
    sendEvent,
    sendEventByTriggerType,
    getEventValuesByOperation,
    t
  } = useCommonForGeneral(props, {
    listenerEventOperation,
    getEventValueCanUseObj
  })

  const status = ref(true)
  const currentValue = ref<any>(null)
  const timer = ref<any>(null)

  const btnStyle = computed(() => {
    const styleMap = {} as any
    // 边框
    if (hasProp(styleProp.value, 'borderStyle')) {
      const { enable, color } = styleProp.value?.borderStyle
      if (enable && color.length) {
        styleMap.border = `1px solid ${color}`
      } else {
        styleMap.border = 'none'
      }
    } else if (!prop.value?.border) {
      styleMap.border = 'none'
    }
    // 填充
    if (!prop.value?.fill && styleProp.value?.background?.enable) {
      styleMap.background = composeColor(styleProp.value?.background.color)
    }
    // 文字内容
    if (styleProp.value?.txtStyle?.enable) {
      styleMap.color = styleProp.value?.txtStyle.color
    }
    return styleMap
  })

  const btnPlain = computed(() => {
    if (!prop.value?.fill) {
      return true
    }
    return false
  })

  const isStatusBtn = computed(() => {
    const e = find(item.events, (et) => et.eventType === 'send' && et.triggerType === 'status')
    return !!e
  })

  const btnType = computed(() => {
    if (prop.value?.type !== 'text') {
      return prop.value?.type
    }
    return 'primary'
  })

  const isText = computed(() => {
    return prop.value?.type === 'text'
  })

  const eventValue = computed(() => {
    // 所有绑定的接收事件
    return buildApiQueryByEvent(getEventValuesByOperation(item.events || []))
  })

  const time = computed(() => {
    return prop.value.timingTime * 60 * 1000
  })

  function showTxt(bool: boolean) {
    return bool ? t('global.hide') : t('global.display')
  }

  function click() {
    const e = find(item.events, (et) => et.eventType === 'send')
    if (e) {
      if (e.triggerType === 'status') {
        status.value = !status.value
        sendEvent(e, status.value)
      } else {
        sendEventByTriggerType(item, e.triggerType!, getEventValueCanUseObj())
      }
    }
  }

  function groupClick(val: any) {
    currentValue.value = val
    const e = find(item.events, (et) => et.eventType === 'send')
    if (e) {
      sendEventByTriggerType(item, e.triggerType!, getEventValueCanUseObj())
    }
  }

  function onRefresh() {
    if (prop.value?.model === 'base') {
      click()
    }
  }

  /**
   * 获取可供交互使用的事件值
   */
  function getEventValueCanUseObj() {
    return {
      eventValue: eventValue.value,
      currentValue: currentValue.value
    }
  }

  /**
   * 根据操作方法执行组件中对应的事件值
   * @param operation 操作方法key
   * @param data 交互值
   */
  function listenerEventOperation(operation: string, operationData: ComponentOperationData) {
    // 设置绑定值
    if (operation === 'onlyReceiveEvent') {
      onlyReceiveEvent()
    }
  }

  watch(
    () => prop.value.timingOpen,
    (nv) => {
      if (nv) {
        if (!timer.value) {
          timer.value = setInterval(() => {
            onRefresh()
          }, time.value)
        }
      } else if (timer.value) {
        clearInterval(timer.value)
        timer.value = null
      }
    },
    {
      immediate: true
    }
  )

  watch(
    () => prop.value.timingTime,
    (nv) => {
      if (prop.value.timingOpen && nv > 0) {
        clearInterval(timer.value)
        timer.value = setInterval(() => {
          onRefresh()
        }, time.value)
      }
    },
    {
      immediate: true
    }
  )

  onMounted(() => {
    if (hasIn(prop.value, 'status')) {
      status.value = prop.value!.status
    }
  })
</script>

<style lang="scss" scoped>
  .button-widge {
    .base {
      width: 100%;

      &.el-button--large {
        --el-button-size: var(--haohan-size-large);
        font-size: 16px;
      }

      &.el-button--default {
        height: var(--haohan-size-default);
      }

      &.el-button--small {
        height: var(--haohan-size-small);
        font-size: 12px;
      }

      :deep(.svg-icon) {
        margin-right: 6px;
      }

      &.underline {
        :deep(span) {
          text-decoration: underline;
        }
      }
    }

    .group {
      :deep(.svg-icon) {
        font-size: inherit;
        margin-right: 6px;
      }
    }

    .el-select {
      width: 178px;
    }
  }
</style>
